import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  Upload, FileText, Trash2, Eye, X, ChevronLeft, ChevronRight,
  FileCheck, Paperclip, Loader2, Pencil, Check, MapPin, Link2
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadZone = ({ docType, label, description, accept, onUpload, uploading }) => {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onUpload(file, docType);
  }, [onUpload, docType]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) onUpload(file, docType);
    e.target.value = "";
  };

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        dragOver ? "border-forest bg-forest/5" : "border-stone hover:border-forest/40"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid={`upload-zone-${docType}`}
    >
      <input
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        disabled={uploading}
        data-testid={`file-input-${docType}`}
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 text-forest animate-spin" />
          <p className="text-sm text-muted-foreground">Uploading...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center">
            <Upload className="w-5 h-5 text-forest" />
          </div>
          <div>
            <p className="font-medium text-forest">{label}</p>
            <p className="text-sm text-muted-foreground mt-1">{description}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const PDFPreviewModal = ({ docId, filename, token, onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        const response = await axios.get(`${API}/docusign/documents/${docId}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          withCredentials: true
        });
        const url = URL.createObjectURL(response.data);
        setPdfUrl(url);
      } catch {
        toast.error("Failed to load document preview");
        onClose();
      } finally {
        setLoading(false);
      }
    };
    fetchPdf();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="pdf-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-4xl h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-forest" />
            <span className="font-medium text-forest truncate max-w-md">{filename}</span>
          </div>
          <div className="flex items-center gap-3">
            {numPages && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  data-testid="pdf-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-mono text-muted-foreground min-w-[80px] text-center">
                  {currentPage} / {numPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage >= numPages}
                  data-testid="pdf-next-page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-pdf-preview">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex-1 overflow-auto flex justify-center p-6 bg-stone/20">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 text-forest animate-spin" />
            </div>
          ) : pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={<Loader2 className="w-8 h-8 text-forest animate-spin" />}
              error={<p className="text-red-600">Failed to load PDF</p>}
            >
              <Page
                pageNumber={currentPage}
                width={700}
                renderTextLayer={true}
                renderAnnotationLayer={true}
              />
            </Document>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const DocuSignDocuments = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${API}/docusign/documents`, { headers, withCredentials: true });
      setDocuments(response.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadDocuments(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (file, docType) => {
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(
        `${API}/docusign/documents?doc_type=${docType}`,
        formData,
        { headers: { ...headers, "Content-Type": "multipart/form-data" }, withCredentials: true }
      );
      setDocuments(prev => [...prev, response.data]);
      toast.success(`${docType === "agreement" ? "Agreement" : "Supporting document"} uploaded`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/docusign/documents/${docId}`, { headers, withCredentials: true });
      setDocuments(prev => prev.filter(d => d.doc_id !== docId));
      toast.success("Document deleted");
    } catch {
      toast.error("Failed to delete document");
    }
  };

  const handleRename = async (docId, displayName) => {
    try {
      await axios.patch(`${API}/docusign/documents/${docId}`, { display_name: displayName }, { headers, withCredentials: true });
      setDocuments(prev => prev.map(d => d.doc_id === docId ? { ...d, display_name: displayName } : d));
      toast.success("Document renamed");
    } catch {
      toast.error("Failed to rename document");
    }
  };

  const handleToggleBundle = async (docId, bundleWithAgreement) => {
    try {
      await axios.patch(`${API}/docusign/documents/${docId}`, { bundle_with_agreement: bundleWithAgreement }, { headers, withCredentials: true });
      setDocuments(prev => prev.map(d => d.doc_id === docId ? { ...d, bundle_with_agreement: bundleWithAgreement } : d));
      toast.success(bundleWithAgreement ? "Document will be included with agreements" : "Document removed from auto-bundle");
    } catch {
      toast.error("Failed to update document settings");
    }
  };

  const agreements = documents.filter(d => d.doc_type === "agreement");
  const supporting = documents.filter(d => d.doc_type === "supporting");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="docusign-documents-tab">
      {/* Agreements Section */}
      <Card className="border-stone">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-forest" />
            Agreement Documents
          </CardTitle>
          <CardDescription>
            Upload your advisory agreement PDFs. These will be used as templates for DocuSign — you'll map signature and data fields onto them in the next step.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <UploadZone
            docType="agreement"
            label="Drop your agreement PDF here, or click to browse"
            description="PDF files only, up to 20MB"
            accept=".pdf"
            onUpload={handleUpload}
            uploading={uploading === "agreement"}
          />
          {agreements.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-xs font-label uppercase tracking-wider text-muted-foreground">
                Uploaded Agreements ({agreements.length})
              </p>
              {agreements.map(doc => (
                <DocumentRow
                  key={doc.doc_id}
                  doc={doc}
                  onPreview={() => setPreviewDoc(doc)}
                  onDelete={() => handleDelete(doc.doc_id)}
                  onRename={(name) => handleRename(doc.doc_id, name)}
                  onMapFields={() => navigate(`/documents/${doc.doc_id}/map`)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Supporting Documents Section */}
      <Card className="border-stone">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-forest" />
            Supporting Documents
          </CardTitle>
          <CardDescription>
            Upload supplementary documents (ADV, Privacy Policy, Disclosures) that will be attached to the DocuSign envelope alongside the agreement.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <UploadZone
            docType="supporting"
            label="Drop supporting documents here, or click to browse"
            description="PDF, DOC, or DOCX files, up to 10MB"
            accept=".pdf,.doc,.docx"
            onUpload={handleUpload}
            uploading={uploading === "supporting"}
          />
          {supporting.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-xs font-label uppercase tracking-wider text-muted-foreground">
                Uploaded Supporting Documents ({supporting.length})
              </p>
              {supporting.map(doc => (
                <DocumentRow
                  key={doc.doc_id}
                  doc={doc}
                  onPreview={doc.file_type === ".pdf" ? () => setPreviewDoc(doc) : null}
                  onDelete={() => handleDelete(doc.doc_id)}
                  onRename={(name) => handleRename(doc.doc_id, name)}
                  onToggleBundle={(value) => handleToggleBundle(doc.doc_id, value)}
                  isSupporting={true}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* PDF Preview Modal */}
      {previewDoc && (
        <PDFPreviewModal
          docId={previewDoc.doc_id}
          filename={previewDoc.filename}
          token={token}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
};

const DocumentRow = ({ doc, onPreview, onDelete, onRename, onMapFields, onToggleBundle, isSupporting }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(doc.display_name || "");
  const displayLabel = doc.display_name || doc.filename;

  const commitRename = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== (doc.display_name || "")) {
      onRename(trimmed);
    }
    setEditing(false);
  };

  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg border border-stone bg-white hover:bg-sand/50 transition-colors"
      data-testid={`document-row-${doc.doc_id}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-md bg-forest/10 flex items-center justify-center shrink-0">
          <FileText className="w-4 h-4 text-forest" />
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1.5">
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditing(false); }}
                onBlur={commitRename}
                className="h-7 text-sm py-0 px-2 w-56"
                maxLength={150}
                data-testid={`rename-input-${doc.doc_id}`}
              />
            </div>
          ) : (
            <div className="flex items-center gap-1.5 group/name">
              <p className="font-medium text-sm text-forest truncate max-w-[200px]">{displayLabel}</p>
              <button
                onClick={() => { setName(doc.display_name || doc.filename.replace(/\.[^/.]+$/, "")); setEditing(true); }}
                className="opacity-0 group-hover/name:opacity-100 transition-opacity"
                data-testid={`rename-btn-${doc.doc_id}`}
              >
                <Pencil className="w-3 h-3 text-muted-foreground hover:text-forest" />
              </button>
            </div>
          )}
          <p className="text-xs text-muted-foreground font-mono">
            {formatFileSize(doc.size)} &middot; {doc.file_type.replace(".", "").toUpperCase()}
            {doc.display_name && <span className="ml-1 text-muted-foreground/60">({doc.filename})</span>}
          </p>
        </div>
        
        {/* Bundle toggle for supporting documents */}
        {isSupporting && onToggleBundle && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-sand/50 rounded-md border border-stone/50">
            <Link2 className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground whitespace-nowrap">Always include</span>
            <Switch
              checked={doc.bundle_with_agreement || false}
              onCheckedChange={onToggleBundle}
              className="scale-75"
              data-testid={`bundle-toggle-${doc.doc_id}`}
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-2">
        {onMapFields && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-forest hover:text-forest hover:bg-forest/10 text-xs gap-1.5 px-2.5"
            onClick={onMapFields}
            data-testid={`map-fields-${doc.doc_id}`}
          >
            <MapPin className="w-3.5 h-3.5" />
            Map Fields
          </Button>
        )}
        {onPreview && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-forest hover:text-forest"
            onClick={onPreview}
            data-testid={`preview-doc-${doc.doc_id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
        )}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-700"
              data-testid={`delete-doc-${doc.doc_id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete document?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{displayLabel}</strong>. Any proposals referencing this document will need to be updated.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default DocuSignDocuments;
